import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Banner, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { useMe } from '../../hooks/useMe.js';
import { scopeKindOf } from '../../lib/scopeKinds.js';
import type { TenantEventType } from '../../lib/types.js';
import { useScopesLogic } from '../scopes/useScopesLogic.js';
import { useApiKeysListLogic } from '../apiKeys/useApiKeysLogic.js';
import { useEventCatalogLogic, useEventCatalogMutations } from './useEventCatalogLogic.js';
import { EventDefinitionForm } from './EventDefinitionForm.js';

export const EventDefinitionPage = () => {
  const { eventTypeId } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const tenantId = me.data?.tenant.id;
  const catalog = useEventCatalogLogic(tenantId);
  const mutations = useEventCatalogMutations(tenantId);
  const { scopes } = useScopesLogic();
  const keys = useApiKeysListLogic(tenantId);
  const [registered, setRegistered] = useState<TenantEventType | null>(null);
  const entry = eventTypeId ? catalog.all.find((e) => e.id === eventTypeId) : null;
  const back = () => {
    void navigate('/events?tab=catalog');
  };

  return (
    <AppShell>
      <Breadcrumbs trail={[{ label: 'Events', to: '/events?tab=catalog' }]} />
      {me.isLoading || catalog.isLoading ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : catalog.error ? (
        <Banner variant="danger">
          <Banner.Body>{catalog.error.message}</Banner.Body>
        </Banner>
      ) : !tenantId || (eventTypeId && !entry) ? (
        <Banner variant="warning">
          <Banner.Body>This event could not be found in your organization.</Banner.Body>
        </Banner>
      ) : (
        <EventDefinitionForm
          key={`${tenantId}:${eventTypeId ?? 'new'}`}
          entry={entry ?? null}
          existing={catalog.all}
          scopeOptions={scopes
            .filter((scope) => scopeKindOf(scope) === 'data' && scope.audience !== 'app')
            .map((scope) => ({ value: scope.key, label: scope.key }))}
          emitKeys={(keys.data ?? []).filter(
            (key) => key.isEnabled && key.scopes.includes('Events.Emit'),
          )}
          registered={registered}
          isBusy={mutations.isBusy}
          error={mutations.error}
          onDone={back}
          onSendTest={(event) => mutations.sendTest(event.id)}
          onBuildWorkflow={(event) => {
            void navigate(`/settings/workflows/new?trigger=${encodeURIComponent(event.type)}`);
          }}
          onSubmit={async (input) => {
            if (entry?.status === 'approved') {
              const { type: _type, ...body } = input;
              await mutations.update(entry.id, body);
              back();
            } else {
              const created = await mutations.register(input);
              if (entry) back();
              else setRegistered(created);
            }
          }}
        />
      )}
    </AppShell>
  );
};
